import { useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, Mic, Square, Trash2, Sparkles, Upload, Info } from "lucide-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";

interface Props {
  orgId: string | null;
  trigger?: React.ReactNode;
}

interface Sample { id: string; blob: Blob; durationMs: number; source: "record" | "upload"; name: string }

const MAX_SAMPLES = 5;
const MIN_SAMPLE_MS = 5_000;
const MAX_SAMPLE_MB = 10;

export function JarvisVoiceCloneDialog({ orgId, trigger }: Props) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [gender, setGender] = useState<"male" | "female" | "">("");
  const [samples, setSamples] = useState<Sample[]>([]);
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const recordStartRef = useRef<number>(0);
  const streamRef = useRef<MediaStream | null>(null);

  const reset = () => {
    setName(""); setDescription(""); setGender(""); setSamples([]);
    if (recording) stopRecording();
  };

  const handleOpen = (v: boolean) => { setOpen(v); if (!v) reset(); };

  const startRecording = async () => {
    if (samples.length >= MAX_SAMPLES) { toast.error("Máximo de 5 amostras"); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "audio/webm";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      const chunks: Blob[] = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(chunks, { type: mime });
        const dur = Date.now() - recordStartRef.current;
        if (dur < MIN_SAMPLE_MS) {
          toast.error(`Grave pelo menos ${MIN_SAMPLE_MS / 1000}s`);
        } else {
          setSamples((prev) => [...prev, { id: crypto.randomUUID(), blob, durationMs: dur, source: "record", name: `gravacao_${prev.length + 1}.webm` }]);
        }
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      };
      recorderRef.current = rec;
      recordStartRef.current = Date.now();
      rec.start();
      setRecording(true);
    } catch (e: any) {
      toast.error(`Microfone bloqueado: ${e.message ?? e}`);
    }
  };

  const stopRecording = () => {
    try { recorderRef.current?.state !== "inactive" && recorderRef.current?.stop(); } catch { /* noop */ }
    recorderRef.current = null;
    setRecording(false);
  };

  const handleUpload = (files: FileList | null) => {
    if (!files) return;
    const arr = Array.from(files);
    for (const f of arr) {
      if (samples.length >= MAX_SAMPLES) break;
      if (f.size > MAX_SAMPLE_MB * 1024 * 1024) {
        toast.error(`${f.name}: maior que ${MAX_SAMPLE_MB}MB`);
        continue;
      }
      setSamples((prev) => [...prev, { id: crypto.randomUUID(), blob: f, durationMs: 0, source: "upload", name: f.name }]);
    }
  };

  const removeSample = (id: string) => setSamples((s) => s.filter((x) => x.id !== id));

  const blobToBase64 = (blob: Blob): Promise<string> => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve((r.result as string).split(",")[1] ?? "");
    r.onerror = () => reject(r.error);
    r.readAsDataURL(blob);
  });

  const submit = async () => {
    if (!orgId) { toast.error("Sem organização"); return; }
    if (!name.trim() || name.length < 2) { toast.error("Nome obrigatório"); return; }
    if (samples.length < 1) { toast.error("Pelo menos 1 amostra"); return; }
    setUploading(true);
    try {
      const payload = {
        org_id: orgId,
        name: name.trim(),
        description: description.trim() || undefined,
        gender: gender || null,
        language: "pt-BR",
        samples: await Promise.all(samples.map(async (s) => ({
          filename: s.name,
          mimetype: s.blob.type || "audio/webm",
          base64: await blobToBase64(s.blob),
        }))),
      };
      const { data, error } = await supabase.functions.invoke("jarvis-voice-clone", { body: payload });
      if (error) throw new Error(error.message || "clone_failed");
      if (data?.error) throw new Error(data.detail || data.error);
      toast.success(`Voz "${data.voice.display_name}" criada!`);
      qc.invalidateQueries({ queryKey: ["jarvis-voices", orgId] });
      handleOpen(false);
    } catch (e: any) {
      toast.error(`Falha ao clonar: ${e.message ?? e}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button variant="outline">
            <Sparkles className="w-4 h-4 mr-2" /> Clonar voz
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Clonar nova voz</DialogTitle>
          <DialogDescription>
            Grave ou envie 1 a 5 amostras (5s+ cada) para criar uma voz personalizada via ElevenLabs.
          </DialogDescription>
        </DialogHeader>

        <Alert>
          <Info className="w-4 h-4" />
          <AlertDescription className="text-xs">
            Voice Cloning consome créditos do plano <strong>ElevenLabs Creator+</strong>. Qualidade depende
            de áudio limpo, sem ruído de fundo, em uma única voz.
          </AlertDescription>
        </Alert>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="vc-name">Nome da voz *</Label>
            <Input id="vc-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex: João, Voz Corporativa…" maxLength={60} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="vc-desc">Descrição</Label>
            <Textarea id="vc-desc" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Tom, sotaque, contexto de uso…" maxLength={500} rows={2} />
          </div>
          <div className="space-y-1.5">
            <Label>Gênero (opcional)</Label>
            <div className="flex gap-2">
              {(["male", "female"] as const).map((g) => (
                <Button key={g} type="button" size="sm" variant={gender === g ? "default" : "outline"} onClick={() => setGender(gender === g ? "" : g)}>
                  {g === "male" ? "Masculino" : "Feminino"}
                </Button>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>Amostras ({samples.length}/{MAX_SAMPLES})</Label>
            <div className="flex gap-2">
              {!recording ? (
                <Button type="button" size="sm" variant="outline" onClick={startRecording} disabled={samples.length >= MAX_SAMPLES}>
                  <Mic className="w-3.5 h-3.5 mr-1.5" /> Gravar
                </Button>
              ) : (
                <Button type="button" size="sm" variant="destructive" onClick={stopRecording}>
                  <Square className="w-3.5 h-3.5 mr-1.5" /> Parar
                </Button>
              )}
              <label>
                <input type="file" accept="audio/*" multiple className="hidden" onChange={(e) => handleUpload(e.target.files)} />
                <Button type="button" size="sm" variant="outline" asChild>
                  <span><Upload className="w-3.5 h-3.5 mr-1.5" /> Upload</span>
                </Button>
              </label>
            </div>
          </div>
          {samples.length === 0 ? (
            <p className="text-xs text-muted-foreground italic">Nenhuma amostra ainda.</p>
          ) : (
            <ul className="space-y-1.5">
              {samples.map((s) => (
                <li key={s.id} className="flex items-center justify-between gap-2 p-2 rounded-md border bg-muted/30 text-xs">
                  <div className="flex items-center gap-2 min-w-0">
                    <Badge variant="outline" className="text-[10px]">{s.source === "record" ? "Gravado" : "Upload"}</Badge>
                    <span className="truncate">{s.name}</span>
                    {s.durationMs > 0 && <span className="text-muted-foreground tabular-nums">{(s.durationMs / 1000).toFixed(1)}s</span>}
                  </div>
                  <Button type="button" size="icon" variant="ghost" className="h-6 w-6" onClick={() => removeSample(s.id)}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => handleOpen(false)} disabled={uploading}>Cancelar</Button>
          <Button onClick={submit} disabled={uploading || samples.length === 0 || !name.trim()}>
            {uploading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enviando…</>) : "Criar voz clonada"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
